/**
 * 素材统计数据 Hook
 *
 * @description 用于获取和处理素材统计核心指标数据，支持环比数据和时间线数据
 *
 * @features
 * - 自动依赖 filter 刷新数据
 * - 支持日环比 和周环比 数据
 * - 支持 Mini 趋势图数据转换
 * - 支持饼图数据转换
 * - 自动取消未完成请求
 *
 * @example
 * ```tsx
 * const { cardData, loading } = useAdStatsData({ filter, gameInfo });
 * // cardData.allCreative - 在投创意数卡片数据
 * // cardData.allMaterial - 在投素材数卡片数据
 * // cardData.newMaterial - 新增素材数卡片数据
 * ```
 *
 * @对应板块 板块 B — 顶部指标卡片区
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { getAdCreativeStats } from '@op/services/adCreative';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_REQ, DBO_V } from '@op/utils/format';
import type { TRequestPieBackType } from '@op/utils/requestUtil';
import useLanguage from '@op/hooks/useLanguage';

// 环比数据（已转换为 RingRatioBubble 所需格式）
interface IChangeData {
  value: number; // 占比: changeValue / mainValue（PercentageItem 使用）
  oldValue: number; // 对比周期原始值（RingRatioBubble 展示用）
  oldTime: string[]; // [old_start_date, old_end_date]
  newTime: string[]; // [start_time, end_time]
}

// 时间线数据项（Mini 趋势图专用）
interface ITimelineItem {
  time: string;
  value: number;
  oldValue?: number;
}

// 单个卡片数据
interface IMetricCardData {
  mainValue: number;
  dod?: IChangeData;
  sdlw?: IChangeData;
  timeline?: ITimelineItem[];
  pieData?: TRequestPieBackType;
}

// Hook 参数
interface IUseAdStatsDataParams {
  filter?: {
    start_time: string;
    end_time: string;
    date_type?: string;
  };
  gameInfo?: {
    edition_unified_id: string;
    id_type: string;
    entity_type: string;
  };
}

// Hook 返回值
interface IUseAdStatsDataResult {
  cardData: {
    allCreative: IMetricCardData;
    allMaterial: IMetricCardData;
    newMaterial: IMetricCardData;
  } | null;
  loading: boolean;
  rawData: any;
  translationMap: Record<string, string>; // 翻译映射
  hasDataDatetime: string | null; // 数据时间戳
}

/**
 * 将饼图原始数据转换为 TRequestPieBackType 格式
 */
function transformPieData(
  pieList?: Array<{
    dimension_value: string;
    dimension_value_en?: string;
    dimension_value_zh?: string;
    value: number;
  }>,
  isEn: boolean = true,
): TRequestPieBackType {
  if (!pieList?.length) {
    return { series: [], legend: [], isEmpty: true, totalSeries: 0 };
  }

  // 排序：other 相关项放最后，其他按值降序
  const sortedList = [...pieList].sort((a, b) => {
    const isAOthers = a.dimension_value.toLowerCase() === 'others';
    const isBOthers = b.dimension_value.toLowerCase() === 'others';

    if (isAOthers && !isBOthers) return 1;
    if (!isAOthers && isBOthers) return -1;
    return (b?.value || 0) - (a?.value || 0);
  });

  const series: Array<{ name: string; value: number }> = [];
  const legend: string[] = [];
  let totalSeries = 0;

  for (const item of sortedList) {
    const value = item?.value || 0;
    const dimensionName = isEn
      ? item.dimension_value_en || item.dimension_value
      : item.dimension_value_zh || item.dimension_value;

    series.push({ name: dimensionName, value });
    legend.push(dimensionName);
    totalSeries += value;
  }

  return {
    series,
    legend,
    isEmpty: totalSeries === 0,
    totalSeries,
  };
}

/**
 * 将时间线数据转换为 Mini 趋势图所需格式
 */
function transformTimelineData(
  timeline?: Array<{ time: string; [key: string]: any }>,
  oldTimeline?: Array<{ time: string; [key: string]: any }>,
  filter?: { date_type: string; start_time: string; end_time: string },
  valueKey?: string,
  timeline_of_start_time?: string,
  timeline_of_end_time?: string,
  old_timeline_of_start_time?: string,
  old_timeline_of_end_time?: string,
): ITimelineItem[] {
  if (!timeline?.length || !filter || !valueKey) return [];

  // 使用 DBO_REQ.moreKeys 处理当前周期数据
  const currentChartData = DBO_REQ.moreKeys({
    timeline,
    timeKey: 'time',
    filter,
    timeline_of_start_time: timeline_of_start_time || filter.start_time,
    timeline_of_end_time: timeline_of_end_time || filter.end_time,
    fixedKeyList: [valueKey],
  });

  // 处理对比周期数据（使用独立的起止时间）
  const oldChartData = oldTimeline?.length
    ? DBO_REQ.moreKeys({
        timeline: oldTimeline,
        timeKey: 'time',
        filter,
        timeline_of_start_time: old_timeline_of_start_time || filter.start_time,
        timeline_of_end_time: old_timeline_of_end_time || filter.end_time,
        fixedKeyList: [valueKey],
      })
    : null;

  // 合并为 ITimelineItem[] 格式
  const xAxis = currentChartData.xAxis || [];
  const currentSeries = currentChartData.seriesObj?.[valueKey] || [];
  const oldSeries = oldChartData?.seriesObj?.[valueKey] || [];

  return xAxis.map((time, index) => ({
    time,
    value: currentSeries[index] ?? 0,
    oldValue: oldSeries[index] ?? undefined,
  }));
}

/**
 * 将环比原始数据转换为 RingRatioBubble 所需格式
 *
 * @param changeItem 环比对象（dod / sdlw）
 * @param total 当前周期数据（total）
 * @param valueKey 数值字段名（'all_creative' | 'all_material' | 'new_material'）
 */
function transformChangeData(
  changeItem: Record<string, any> | undefined,
  total: Record<string, any> | undefined,
  valueKey: string,
): IChangeData | undefined {
  const changeValue = changeItem?.[valueKey];
  const mainValue = total?.[valueKey];
  const linkTime = changeItem?.link_time;

  if (changeValue === undefined || mainValue === undefined) return undefined;

  return {
    value: DBO_V.qoq(mainValue, changeValue) || 0,
    oldValue: changeValue,
    oldTime: linkTime ? [linkTime.old_start_date, linkTime.old_end_date] : [],
    newTime: linkTime ? [linkTime.new_start_date, linkTime.new_end_date] : [],
  };
}

export const useAdStatsData = (
  params: IUseAdStatsDataParams,
): IUseAdStatsDataResult => {
  const { filter, gameInfo } = params;

  const [data, setData] = useState<any>(null);
  const { isEn } = useLanguage();

  const { loading, wrapperCancelRequest } = useCancelRequestHook<any>({
    clear: () => {
      setData(null);
    },
  });

  const fetchData = useCallback(async () => {
    if (!filter?.start_time || !filter?.end_time || !gameInfo) {
      return;
    }

    try {
      const res = await wrapperCancelRequest(getAdCreativeStats, {
        edition_unified_id: gameInfo.edition_unified_id,
        id_type: gameInfo.id_type,
        entity_type: gameInfo.entity_type,
        start_time: filter.start_time,
        end_time: filter.end_time,
        date_type: filter.date_type || 'daily',
        need_dod: true,
        need_sdlw: true,
      });

      setData(res);
    } catch (err) {
      console.error('获取素材统计数据失败:', err);
    }
  }, [filter, gameInfo, wrapperCancelRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 数据格式化
  const cardData = useMemo(() => {
    if (!data) return null;

    const {
      total,
      dod,
      sdlw,
      timeline,
      old_timeline,
      all_creative_type_list,
      timeline_of_start_time,
      timeline_of_end_time,
      old_timeline_of_start_time,
      old_timeline_of_end_time,
    } = data;

    // 构建过滤条件
    const chartFilter = {
      date_type: filter?.date_type || 'daily',
      start_time: filter?.start_time,
      end_time: filter?.end_time,
    };

    return {
      // 卡片1：在投创意数（饼图）
      allCreative: {
        mainValue: total?.all_creative,
        dod: transformChangeData(dod, total, 'all_creative'),
        sdlw: transformChangeData(sdlw, total, 'all_creative'),
        pieData: transformPieData(all_creative_type_list, isEn),
      },

      // 卡片2：在投素材数（趋势图）
      allMaterial: {
        mainValue: total?.all_material,
        dod: transformChangeData(dod, total, 'all_material'),
        sdlw: transformChangeData(sdlw, total, 'all_material'),
        timeline: transformTimelineData(
          timeline,
          old_timeline,
          chartFilter,
          'all_material',
          timeline_of_start_time,
          timeline_of_end_time,
          old_timeline_of_start_time,
          old_timeline_of_end_time,
        ),
      },

      // 卡片3：新增素材数（趋势图）
      newMaterial: {
        mainValue: total?.new_material,
        dod: transformChangeData(dod, total, 'new_material'),
        sdlw: transformChangeData(sdlw, total, 'new_material'),
        timeline: transformTimelineData(
          timeline,
          old_timeline,
          chartFilter,
          'new_material',
          timeline_of_start_time,
          timeline_of_end_time,
          old_timeline_of_start_time,
          old_timeline_of_end_time,
        ),
      },
    };
  }, [data, filter, isEn]);

  // ==================== 翻译映射 ====================
  const translationMap = useMemo(() => {
    if (!data?.all_creative_type_list) return {};

    const map: Record<string, string> = {};
    for (const item of data.all_creative_type_list) {
      const key = item.dimension_value;
      const translatedName = isEn
        ? item.dimension_value_en || item.dimension_value
        : item.dimension_value_zh || item.dimension_value;
      map[key] = translatedName;
    }
    return map;
  }, [data, isEn]);

  return {
    cardData,
    loading,
    rawData: data,
    translationMap,
    hasDataDatetime: data?.has_data_datetime ?? null,
  };
};

export default useAdStatsData;
